import { Card, Typography, Button, Space, Tooltip } from "antd";
import { Bar } from "react-chartjs-2";
import ChartDataLabels from "chartjs-plugin-datalabels";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip as ChartTooltip,
  Legend,
} from "chart.js";
import { ExpandOutlined, PictureOutlined, FileExcelOutlined } from "@ant-design/icons";
import { useState, useRef } from "react";
import dayjs from "dayjs";
import * as XLSX from "xlsx";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ChartTooltip,
  Legend,
  ChartDataLabels
);

const { Title } = Typography;

const COLOR_CON = "#024589";
const COLOR_SIN = "#F4A000";

export function MunicipiosChart({ data = [] }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const chartRef = useRef(null);

  if (!data || data.length === 0) {
    return (
      <Card hoverable style={{ borderRadius: 12 }}>
        <Title level={5}>Encuestas por Municipio</Title>
        <p>No hay datos disponibles.</p>
      </Card>
    );
  }

  const totalGeneral      = data.reduce((s, d) => s + d.total_encuestas,        0);
  const totalConProyecto  = data.reduce((s, d) => s + d.encuestas_con_proyecto, 0);
  const totalSinProyecto  = data.reduce((s, d) => s + d.encuestas_sin_proyecto, 0);

  const chartData = {
    labels: data.map((d) => d.municipio),
    datasets: [
      {
        label: "Con proyecto",
        data: data.map((d) => d.encuestas_con_proyecto),
        backgroundColor: COLOR_CON,
        borderColor:     COLOR_CON,
        borderWidth:     0,
        borderRadius:    0,   // ← sin redondeo: este dataset queda debajo en el stack
        stack: "stack",
        datalabels: {
          display:   (ctx) => ctx.dataset.data[ctx.dataIndex] > 0,
          color:     "#fff",
          font:      { weight: "bold", size: 10 },
          formatter: (v) => v.toLocaleString(),
        },
      },
      {
        label: "Sin proyecto",
        data: data.map((d) => d.encuestas_sin_proyecto),
        backgroundColor: COLOR_SIN,
        borderColor:     COLOR_SIN,
        borderWidth:     0,
        borderRadius:    4,   // ← solo el dataset superior lleva borderRadius
        stack: "stack",
        datalabels: {
          display:   (ctx) => ctx.dataset.data[ctx.dataIndex] > 0,
          color:     "#fff",
          font:      { weight: "bold", size: 10 },
          formatter: (v) => v.toLocaleString(),
        },
      },
    ],
  };

  const options = {
    indexAxis: "y",
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display:  true,
        position: "top",
        labels:   { usePointStyle: true, padding: 20 },
      },
      tooltip: {
        callbacks: {
          label: (ctx) => {
            const idx   = ctx.dataIndex;
            const total = data[idx].total_encuestas;
            const pct   = total > 0 ? ((ctx.parsed.x / total) * 100).toFixed(1) : 0;
            return ` ${ctx.dataset.label}: ${ctx.parsed.x.toLocaleString()} (${pct}% del municipio)`;
          },
          footer: (items) => {
            const idx   = items[0].dataIndex;
            const total = data[idx].total_encuestas;
            const pct   = ((total / totalGeneral) * 100).toFixed(1);
            return `Total municipio: ${total.toLocaleString()} (${pct}% del global)`;
          },
        },
      },
      datalabels: {
        anchor: "center",
        align:  "center",
      },
    },
    scales: {
      x: {
        stacked:      true,
        beginAtZero:  true,
        grid:         { color: "rgba(0,0,0,0.05)" },
        ticks:        { callback: (v) => Number(v).toLocaleString() },
      },
      y: {
        stacked: true,
        grid:    { display: false },
        ticks:   { font: { size: 11 } },
      },
    },
  };

  const exportImage = () => {
    if (!chartRef.current) return;
    const canvas = chartRef.current.canvas;
    const exp    = document.createElement("canvas");
    const ctx    = exp.getContext("2d");
    exp.width    = canvas.width  * 2;
    exp.height   = canvas.height * 2;
    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(0, 0, exp.width, exp.height);
    ctx.scale(2, 2);
    ctx.drawImage(canvas, 0, 0);
    const link  = document.createElement("a");
    link.href   = exp.toDataURL("image/png", 1.0);
    link.download = `municipios_${dayjs().format("YYYY-MM-DD")}.png`;
    link.click();
  };

  const exportXLSX = () => {
    const filas = data.map((d, i) => ({
      "#":               i + 1,
      Municipio:         d.municipio,
      "Con Proyecto":    d.encuestas_con_proyecto,
      "Sin Proyecto":    d.encuestas_sin_proyecto,
      "Total Encuestas": d.total_encuestas,
      "% del Total":     totalGeneral > 0
        ? +((d.total_encuestas / totalGeneral) * 100).toFixed(2)
        : 0,
    }));

    filas.push({
      "#":               "",
      Municipio:         "TOTAL",
      "Con Proyecto":    totalConProyecto,
      "Sin Proyecto":    totalSinProyecto,
      "Total Encuestas": totalGeneral,
      "% del Total":     100,
    });

    const hoja  = XLSX.utils.json_to_sheet(filas);
    const libro = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(libro, hoja, "Municipios");
    hoja["!cols"] = [
      { wch: 5  },
      { wch: 30 },
      { wch: 15 },
      { wch: 15 },
      { wch: 16 },
      { wch: 12 },
    ];
    XLSX.writeFile(libro, `municipios_${dayjs().format("YYYY-MM-DD")}.xlsx`);
  };

  const chartHeight = Math.max(400, data.length * 40);

  return (
    <Card
      hoverable
      style={{
        borderRadius: 12,
        ...(isExpanded && {
          position:        "fixed",
          top:             0,
          left:            0,
          right:           0,
          bottom:          0,
          zIndex:          1000,
          margin:          20,
          backgroundColor: "white",
          overflowY:       "auto",   // ← scroll en el Card completo cuando está expandido
        }),
      }}
    >
      {/* ── Encabezado ───────────────────────────────────────────────────── */}
      <div
        style={{
          display:        "flex",
          justifyContent: "space-between",
          alignItems:     "flex-start",
          marginBottom:   16,
          flexWrap:       "wrap",
          gap:            16,
        }}
      >
        <div>
          <Title level={4} style={{ margin: 0 }}>
            Encuestas por Municipio
          </Title>
          <div
            style={{
              fontSize:  14,
              color:     "#666",
              marginTop: 4,
              display:   "flex",
              gap:       16,
              flexWrap:  "wrap",
            }}
          >
            <span>
              {data.length} municipios · {totalGeneral.toLocaleString()} encuestas
            </span>
            <span style={{ color: COLOR_CON, fontWeight: 600 }}>
              Con proyecto: {totalConProyecto.toLocaleString()}
            </span>
            <span style={{ color: COLOR_SIN, fontWeight: 600 }}>
              Sin proyecto: {totalSinProyecto.toLocaleString()}
            </span>
          </div>
        </div>

        <Space>
          <Tooltip title="Exportar imagen">
            <Button icon={<PictureOutlined />} onClick={exportImage} size="small" type="primary">
              Imagen
            </Button>
          </Tooltip>
          <Tooltip title="Exportar Excel">
            <Button icon={<FileExcelOutlined />} onClick={exportXLSX} size="small">
              Excel
            </Button>
          </Tooltip>
          <Tooltip title={isExpanded ? "Minimizar" : "Maximizar"}>
            <Button
              icon={<ExpandOutlined />}
              onClick={() => setIsExpanded(!isExpanded)}
              size="small"
              type={isExpanded ? "primary" : "default"}
            />
          </Tooltip>
        </Space>
      </div>

      {/* ── Gráfico ───────────────────────────────────────────────────────── */}
      <div
        style={{
          maxHeight:       isExpanded ? "calc(100vh - 160px)" : 450,  // ← altura corregida
          overflowY:       "auto",
          backgroundColor: "#fff",
        }}
      >
        <div style={{ height: chartHeight }}>
          <Bar ref={chartRef} data={chartData} options={options} />
        </div>
      </div>
    </Card>
  );
}
